import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from './client.js';

export interface Me {
  user: { id: string; email: string; display_name: string; is_platform_admin: boolean };
  active_tenant_id: string | null;
  role: string | null;
  permissions: string[];
  school_scope: string[];
  tenants: Array<{ id: string; name: string; slug: string; role: string }>;
}

interface AuthContextValue {
  me: Me | null;
  loading: boolean;
  error: unknown;
  can: (permission: string) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();

  const meQuery = useQuery({
    queryKey: ['me'],
    queryFn: () => get<Me>('/auth/me'),
    retry: false,
    staleTime: 30_000,
  });

  const loginMutation = useMutation({
    mutationFn: (vars: { email: string; password: string }) => post('/auth/login', vars),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const logoutMutation = useMutation({
    mutationFn: () => post('/auth/logout'),
    onSuccess: () => queryClient.clear(),
  });

  const switchMutation = useMutation({
    mutationFn: (tenantId: string) => post('/auth/active-tenant', { tenant_id: tenantId }),
    onSuccess: () => queryClient.invalidateQueries(),
  });

  const me = meQuery.data ?? null;

  // Permission checks here are for hiding controls the user cannot use. The
  // server checks every operation independently (§6) — this is never the gate.
  const can = useCallback(
    (permission: string) => Boolean(me?.permissions.includes(permission)),
    [me],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      me,
      loading: meQuery.isLoading,
      error: meQuery.error,
      can,
      login: async (email, password) => {
        await loginMutation.mutateAsync({ email, password });
      },
      logout: async () => {
        await logoutMutation.mutateAsync();
      },
      switchTenant: async (tenantId) => {
        await switchMutation.mutateAsync(tenantId);
      },
    }),
    [me, meQuery.isLoading, meQuery.error, can, loginMutation, logoutMutation, switchMutation],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
